import React, { useMemo, useCallback } from 'react'
import styled from 'styled-components'
import {
  borderRadius,
  heading,
  mediaQuery,
  spacing,
  typographyMixin,
} from 'src/constants/marketing-ds'
import { useCtaText } from 'src/modules/hooks/useCtaText'
import { useSelector } from 'react-redux'
import { selectCTAParams } from 'src/modules/reducers/params'
import { defaultCTAURI, signInURI } from 'src/modules/utils/constants'
import { createUrl } from 'src/modules/utils/createUrl'
import { replaceUrlParam } from 'src/modules/utils/replaceUrlParam'
import CTAButton from '../CTAButtonNew'
import { fireUIInteractionEvent } from 'src/modules/analytics/v3'
import { selectStateForProtobuf } from 'src/modules/reducers'

const ANALYTICS_COMPONENT_SIGNIN = 'header_signin_cta_clicked'
const ANALYTICS_COMPONENT_SIGNUP = 'header_signup_cta_clicked'
const URL_COMP_PARAM = 'header'
const ADDONS_PARAM_KEY = 'addons[]'

const SIGN_IN_TEXT = {
  en: 'Sign in',
  es: 'Acceder',
}

const HeaderSignIn = ({ lang, market, disableFreeTrial, addonCode, offerCode, ctaText }) => {
  const ctaCopy = useCtaText({ lang, market, disableFreeTrial, isTextShort: true, ctaText })
  const parsedParams = useSelector(selectCTAParams)
  const stateForProtobuf = useSelector(selectStateForProtobuf)

  // Memoize URL construction to avoid unnecessary recalculations
  const ctaURL = useMemo(() => {
    const baseURL = createUrl(defaultCTAURI, parsedParams)
    const fallbackParams = {}

    // Build fallback params if needed
    if (offerCode) {
      fallbackParams.offer = offerCode
    }
    if (addonCode) {
      fallbackParams[ADDONS_PARAM_KEY] = addonCode
    }

    // Use fallback params if they're not already in parsedParams
    const shouldUseFallback =
      (!parsedParams?.offerCode && fallbackParams.offer) ||
      (!parsedParams?.addonCode && fallbackParams[ADDONS_PARAM_KEY])

    if (shouldUseFallback) {
      return createUrl(defaultCTAURI, { ...fallbackParams, ...parsedParams })
    }

    return baseURL
  }, [parsedParams, offerCode, addonCode])

  // Memoize the final CTA URL with component parameter
  const ctaHeroURL = useMemo(() => replaceUrlParam(ctaURL, 'comp', URL_COMP_PARAM), [ctaURL])

  // Memoize click handler to prevent unnecessary re-renders
  const handleOnClick = useCallback((isSignIn, text, link) => {
    const isAnchor = link.startsWith('#')
    fireUIInteractionEvent(
      {
        element: text,
        component: isSignIn ? ANALYTICS_COMPONENT_SIGNIN : ANALYTICS_COMPONENT_SIGNUP,
        type: isAnchor ? 'clicked_anchor' : 'clicked',
      },
      stateForProtobuf
    )
  }, [])

  return (
    <Container className="header-sign-in">
      <ContainerInner>
        <CTAButton
          className="header-sign-in-button"
          link={signInURI}
          onClick={() => handleOnClick(true, SIGN_IN_TEXT[lang], signInURI)}
        >
          {SIGN_IN_TEXT[lang]}
        </CTAButton>
        <CTAButton
          className="header-start-button"
          link={ctaHeroURL}
          onClick={() => handleOnClick(false, ctaCopy, ctaHeroURL)}
        >
          {ctaCopy}
        </CTAButton>
      </ContainerInner>
    </Container>
  )
}

export default HeaderSignIn

const Container = styled.header`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  background-color: #000;
  min-height: 72px;
  padding: ${spacing.xs};
  box-sizing: border-box;
  ${mediaQuery.tablet} {
    padding: ${spacing.sm} ${spacing.lg};
  }
  ${mediaQuery.desktop} {
    padding: ${spacing.sm} ${spacing.xl};
  }

  .CTAButton {
    ${typographyMixin(heading.xs)}
    padding: ${spacing.xxs} ${spacing.xs};

    ${mediaQuery.tablet} {
      padding: ${spacing.xxs} ${spacing.lg};
    }

    border-radius: ${borderRadius.xs};
    &.header-sign-in-button {
      background-color: #000;
    }
  }
`

const ContainerInner = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${spacing.xxxs};
  max-width: 1400px;
`
